<template>
  <div class="single-upload-form">
    <!-- Title panel -->
    <div class="title-panel">
      <div>
        <h3>{{ $t('documentUpload.singleUpload') }} {{ referenceNumber }}</h3>
        <div v-if="isDigitalizationProjectMode">
          {{ $t('documentUpload.disabledCheckBotInspection') }}:
          {{ $t(`generic.${isCheckBotInspectionDisabled ? 'yes' : 'no'}`) }}
        </div>
      </div>
      <p class="description">
        {{ $t('documentUpload.singleUploadDescription') }}
      </p>
    </div>
    <single-upload-form
      :content-type-fields="contentTypeFields"
      :content-type-id="contentTypeId"
      :is-busy="isUploading || isCheckingRenditionFile"
      :is-uploading="isUploading"
      :is-rendition-file-required="isRenditionFileRequired"
      :no-required-fields="isDigitalizationProjectMode"
      :upload-progress="uploadProgress"
      :check-file-duplicate="
        isDigitalizationProjectMode ? checkFileDuplicate : undefined
      "
      @form-submit="handleFormSubmit"
      @cancel="goToProjectDetails"
    />
  </div>
</template>

<script lang="ts" setup>
  import {
    IFileMetadata,
    ProjectType,
    DocumentKind,
    ProdisDuplicateResponse,
  } from 'types';
  import {
    ContentTypeFieldType,
    IContentTypeField,
    UniversalMappingField,
  } from 'types';
  import { computed, ref, toRef } from 'vue';
  import { UploadType } from '../MassUploadTable/MassUploadTable.types';
  import { ProjectDetailsTab } from '@/views/ProjectDetails/ProjectDetails.types';
  import { DigitalizationProjectDetailsTab } from '@/views/DigitalizationProjectDetails/DigitalizationProjectDetails.types';
  import { useNavigator } from '@/composables/useNavigator';
  import { useToast } from '@/composables/useToast';
  import { useI18n } from 'vue-i18n';
  import { useMassUpload } from '@/components/MassUpload/composables';
  import { IUploadRecord } from '@/components/MassUpload/MassUpload.types';
  import { FormSubmitEvent } from '@primevue/forms/form';
  import axios from 'axios';
  import { endpointsList } from '@/helpers';
  import { SingleUploadFormSubmitEvent } from '../SingleUploadForm/SingleUploadForm.types';
  import { QuickXorHash } from '@/utils/xorHasher';
  import { prodisPlatformUrls } from '@/static/platform-urls';
  import { IVueexState } from '@/store';
  import { useStore } from 'vuex';

  const props = defineProps<{
    mode: ProjectType;
    contentTypeFields: IContentTypeField[];
    checkBotConfigId?: string;
    referenceNumber: string;
    contentTypeId: string;
    projectId: string;
    supplierId?: string;
    isCheckBotInspectionDisabled?: boolean;
  }>();

  const { createSuccessToast, createErrorToast } = useToast();
  const { t } = useI18n();
  const { goToProjectDetailsTab } = useNavigator();
  const store = useStore<IVueexState>();

  const isCheckingRenditionFile = ref(false);
  const isUploading = ref(false);

  /**
   * This prop needs to be converted to a ref as it is used in a composable.
   * Initially it is undefined and changes later to a value extracted from the
   * route query.
   */
  const isCheckBotInspectionDisabled = toRef(
    props,
    'isCheckBotInspectionDisabled',
    false,
  );

  const uploadRecord = ref<IUploadRecord[]>([]);
  const { uploadFilesAndMetadata, uploadProgress } = useMassUpload(
    uploadRecord,
    props.projectId,
    props.supplierId,
    props.referenceNumber,
    props.contentTypeId,
    props.checkBotConfigId,
    isCheckBotInspectionDisabled,
  );

  const isDigitalizationProjectMode = computed(
    () => props.mode === ProjectType.DIGITALIZATION_PROJECT,
  );

  async function isRenditionFileRequired(documentId: string): Promise<boolean> {
    try {
      isCheckingRenditionFile.value = true;
      const response = await axios.get<boolean>(
        endpointsList.uploadHandler.isRenditionFileExpected(
          documentId,
          props.projectId,
          props.supplierId,
        ),
      );
      return response.data;
    } finally {
      isCheckingRenditionFile.value = false;
    }
  }

  async function checkFileDuplicate(
    file: File,
    _kind: DocumentKind,
  ): Promise<{ isDuplicate: boolean; daisyLinks: string[] }> {
    try {
      const hash = await QuickXorHash.fromFile(file);
      const response = await axios.get<ProdisDuplicateResponse>(
        endpointsList.uploadHandler.getDublicates(),
        { params: { xorHash: hash, projectId: props.projectId } },
      );
      const docs = response.data;
      return {
        isDuplicate: docs.fileDuplicates.length > 0,
        daisyLinks: docs.fileDuplicates.map(
          (doc) =>
            `${prodisPlatformUrls[store.state.env!]}/documents/detail/${doc._id}`,
        ),
      };
    } catch {
      return { isDuplicate: false, daisyLinks: [] };
    }
  }

  async function handleFormSubmit(
    event: SingleUploadFormSubmitEvent,
  ): Promise<void> {
    await upload(
      event.formEvent,
      event.formEvent.values[UniversalMappingField.DOCUMENT_ID],
      event.mainFile,
      event.renditionFile,
    );
  }

  /**
   * Creates an upload record for compatibility with `useMassUpload` composable.
   */
  function createUploadRecord(
    event: FormSubmitEvent,
    documentId: string,
    mainFile: File,
    renditionFile?: File,
  ): IUploadRecord {
    const uploadRecord: IUploadRecord = {
      uploadType: UploadType.NEW_DOCUMENT,
      file: mainFile,
      rawMetadata: {},
      metadata: event.values as IFileMetadata,
    };
    /**
     * The dates in metadata are stored in US format that is accepted by
     * SharePoint.
     */
    Object.entries(event.values).forEach(([key, value]) => {
      const field = props.contentTypeFields
        .filter((f) => f.field !== UniversalMappingField.FILEPATH)
        .find((f) => f.field === key);
      if (field?.dataType === ContentTypeFieldType.DATE_TIME && value) {
        uploadRecord.metadata![key] = new Date(value).toLocaleDateString(
          'en-US',
        );
      }
    });
    if (renditionFile) {
      uploadRecord.renditionFile = {
        file: renditionFile,
        metadata: {
          /** The documentId has to be copied to rendition file metadata */
          documentId,
        } as IFileMetadata,
      };
    }
    /**
     * `documentId` can be autogenerated in digitalization project mode. In
     * this case it has to be set here (it's not present in the form values).
     */
    if (!uploadRecord.metadata?.documentId) {
      uploadRecord.metadata!.documentId = documentId;
    }
    return uploadRecord;
  }

  async function upload(
    event: FormSubmitEvent,
    documentId: string,
    mainFile: File,
    renditionFile?: File,
  ): Promise<void> {
    if (isUploading.value) return;
    uploadRecord.value = [
      createUploadRecord(event, documentId, mainFile, renditionFile),
    ];
    try {
      isUploading.value = true;
      await uploadFilesAndMetadata();
      createSuccessToast(t('documentUpload.uploadSuccessfull', 1));
      goToProjectDetails();
    } catch {
      createErrorToast(t('documentUpload.errors.uploadFailed'));
    } finally {
      isUploading.value = false;
    }
  }

  function goToProjectDetails() {
    goToProjectDetailsTab(
      props.mode,
      props.projectId,
      ProjectDetailsTab.UPLOADS,
      DigitalizationProjectDetailsTab.UPLOADS,
    );
  }
</script>
<style lang="scss" scoped>
  .single-upload-form {
    padding-top: 30px;
    .title-panel {
      margin-bottom: 30px;
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: center;
      h3 {
        font-weight: 700;
        font-size: 36px;
      }
      .description {
        justify-self: center;
      }
    }
  }
</style>
